import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError, of } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { MatDialog } from '@angular/material/dialog';
import { PermissionDeniedDialogComponent } from './permission-denied-dialog.component';
import { ConfigService } from '../services/config.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  constructor(private kc: KeycloakService, private dialog: MatDialog, private cfg: ConfigService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          const status = err.status;
          const body: any = err.error || {};
          const msg = (body?.message || body?.error || '').toString().toLowerCase();
          const missingBearer = msg.includes('missing bearer') || msg.includes('bearer token');

          if (status === 403) {
            // Show permission denied dialog, do not redirect
            try { this.dialog.open(PermissionDeniedDialogComponent, { width: '380px' }); } catch {}
            return throwError(() => err);
          }

          if (status === 401 || missingBearer) {
            // If Keycloak is not configured, avoid attempting login
            const enabled = !!(this.cfg as any).keycloakUrl && !!(this.cfg as any).keycloakRealm && !!(this.cfg as any).keycloakClientId;
            if (!enabled || !this.kc) {
              console.warn('[AuthErrorInterceptor] 401 encountered but Keycloak is not configured; skipping login');
              return throwError(() => err);
            }
            // Si ya estamos en la ruta de login, no navegar de nuevo
            const currentPath = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
            const alreadyOnLogin = currentPath.startsWith('/login');
            // Try silent token refresh first if already logged in
            let isLoggedInResult: any;
            try {
              isLoggedInResult = (this.kc as any).isLoggedIn ? (this.kc as any).isLoggedIn() : Promise.resolve(false);
            } catch {
              isLoggedInResult = Promise.resolve(false);
            }
            const logged$ = isLoggedInResult && typeof isLoggedInResult.then === 'function'
              ? from(isLoggedInResult as Promise<boolean>)
              : of(!!isLoggedInResult as boolean);

            return logged$.pipe(
              switchMap((logged) => {
                if (logged) {
                  return from((this.kc as any).updateToken ? this.kc.updateToken(30) : Promise.resolve()).pipe(
                    switchMap(() => {
                      // After refresh, retry original request once
                      const cloned = req.clone();
                      return next.handle(cloned);
                    }),
                    catchError(() => {
                      // If refresh fails, go to custom login route
                      if (!alreadyOnLogin) {
                        try {
                          const returnUrl = (typeof window !== 'undefined' && window.location) ? (window.location.pathname + window.location.search) : '';
                          this.router.navigate(['/login'], { queryParams: { returnUrl } });
                        } catch {}
                      }
                      return throwError(() => err);
                    })
                  );
                } else {
                  // Not logged: go to custom login route
                  if (!alreadyOnLogin) {
                    try {
                      const returnUrl = (typeof window !== 'undefined' && window.location) ? (window.location.pathname + window.location.search) : '';
                      this.router.navigate(['/login'], { queryParams: { returnUrl } });
                    } catch {}
                  }
                  return throwError(() => err);
                }
              })
            );
          }
        }
        return throwError(() => err);
      })
    );
  }
}
